// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders  (route /orders — "My Orders")
// The signed-in customer's orders in two tabs, Active (placed → out for delivery) and Past (delivered / cancelled), each
// with its count, a search over order ids and item names and a status filter. Every card shows the order id, its real
// status and when it was placed / delivered / cancelled, the items (photo, pack · cut, qty, price), how it was paid and
// the total. Active cards add what is happening now and a Cancel button while ZenFresh hasn't confirmed the order yet;
// Past cards add the refund status of a cancelled order and, for delivered orders, Rate (or the customer's own
// star rating once reviewed). Every card links to the order page, the invoice and (past orders) the Re-order page. The list refreshes itself while there are active orders. Data: utils/orders.ts.
// Deliberately NOT here (nothing supports it): temperature readouts, Track Live/Van, delivery-window promises, thermal-box credit / cashback, cold-chain guarantee banners, "Express 45-min" slots.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, CircleCheck, Clock, FileText, Receipt, RotateCcw, Search, SlidersHorizontal, Star, X } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import CancelOrder from "../components/orders/CancelOrder";
import { OrderLines, RefundNote, StatusBadge } from "../components/orders/OrderParts";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { STATUS_LABEL, STEPS, fetchOrders, formatWhen, headline, packCount, paidVia, type Order, type OrderStatus } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";

type Tab = "active" | "past";
type Load = { status: "loading" } | { status: "error" } | { status: "ready"; orders: Order[] };
const REFRESH_MS = 30_000;
const ACTIVE_STATUSES: OrderStatus[] = ["placed", "confirmed", "preparing", "out_for_delivery"];
const PAST_STATUSES: OrderStatus[] = ["delivered", "cancelled"];

export default function Orders() {
  useDocumentTitle("My Orders");
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [tab, setTab] = useState<Tab>("active");
  const [tabChosen, setTabChosen] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">("all");

  const refresh = useCallback(async () => {
    try {
      setLoad({ status: "ready", orders: await fetchOrders() });
    } catch {
      setLoad((current) => (current.status === "ready" ? current : { status: "error" }));
    }
  }, []);

  useEffect(() => {
    if (signedIn) void refresh();
  }, [signedIn, refresh]);

  const orders = useMemo(() => (load.status === "ready" ? load.orders : []), [load]);
  const hasActive = orders.some((order) => order.active);
  useEffect(() => {
    if (!hasActive) return;
    const timer = window.setInterval(() => document.visibilityState === "visible" && void refresh(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [hasActive, refresh]);

  // Until the customer picks a tab, open on the one that has something in it (Active if any, else Past).
  useEffect(() => {
    if (load.status === "ready" && !tabChosen) setTab(load.orders.some((order) => order.active) || load.orders.length === 0 ? "active" : "past");
  }, [load, tabChosen]);

  const replace = (updated: Order) =>
    setLoad((current) => (current.status === "ready" ? { status: "ready", orders: current.orders.map((order) => (order.ref === updated.ref ? updated : order)) } : current));

  const counts = { active: orders.filter((o) => o.active).length, past: orders.filter((o) => !o.active).length };
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return orders.filter(
      (order) =>
        (tab === "active") === order.active &&
        (statusFilter === "all" || order.status === statusFilter) &&
        (needle === "" || order.ref.toLowerCase().includes(needle) || order.items.some((line) => `${line.name} ${line.cut}`.toLowerCase().includes(needle))),
    );
  }, [orders, tab, query, statusFilter]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const choose = (next: Tab) => {
    setTab(next);
    setTabChosen(true);
    setStatusFilter("all");
  };

  const tabClass = (on: boolean) =>
    `flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full px-4 text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
      on ? "bg-ocean-800 text-white" : "text-ocean-900 hover:bg-ocean-700/5 dark:text-foam-100"
    }`;
  const secondary =
    "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-bold text-ocean-900 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-foam-50";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/menu" aria-label="Back to the menu" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">My Orders</h1>
          </header>

          <div className="flex gap-2">
            <div role="search" className="relative flex-1">
              <label htmlFor="order-search" className="sr-only">Search your orders</label>
              <Search className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
              <input
                id="order-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search orders or catches…"
                autoComplete="off"
                className="h-12 w-full rounded-2xl bg-white pl-12 pr-10 text-base text-ocean-950 shadow-sm outline-none ring-1 ring-ocean-900/10 placeholder:text-ocean-800/50 focus:ring-2 focus:ring-teal-600 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10"
              />
              {query && (
                <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full hover:bg-ocean-700/10">
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="relative">
              <label htmlFor="order-status-filter" className="sr-only">Filter by status</label>
              <SlidersHorizontal className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
              <select
                id="order-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as OrderStatus | "all")}
                className="h-12 w-36 appearance-none rounded-2xl bg-white pl-11 pr-2 text-sm font-semibold text-ocean-950 shadow-sm outline-none ring-1 ring-ocean-900/10 focus:ring-2 focus:ring-teal-600 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10"
              >
                <option value="all">All</option>
                {(tab === "active" ? ACTIVE_STATUSES : PAST_STATUSES).map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </div>
          </div>

          <div role="tablist" aria-label="Order status" className="flex rounded-full bg-white p-1 shadow-sm ring-1 ring-ocean-900/10 dark:bg-ocean-900 dark:ring-white/10">
            {(["active", "past"] as const).map((id) => (
              <button key={id} type="button" role="tab" id={`tab-${id}`} aria-selected={tab === id} aria-controls="orders-panel" onClick={() => choose(id)} className={tabClass(tab === id)}>
                {id === "active" ? "Active Orders" : "Past Orders"}
                <span className={`rounded-full px-2 text-xs ${tab === id ? "bg-white/20" : "bg-ocean-700/10 dark:bg-white/10"}`}>{counts[id]}</span>
              </button>
            ))}
          </div>

          <div id="orders-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} className="flex flex-col gap-4">
            {load.status === "loading" && <p role="status" className="rounded-2xl bg-white p-6 text-center text-sm shadow-sm dark:bg-ocean-900">Loading your orders…</p>}
            {load.status === "error" && (
              <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
                <p className="font-display text-lg font-bold">We couldn&apos;t load your orders</p>
                <button type="button" onClick={() => void refresh()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">Try again</button>
              </div>
            )}
            {load.status === "ready" && shown.length === 0 && (
              <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
                <Receipt className="mx-auto h-10 w-10 text-ocean-800/30 dark:text-foam-100/30" aria-hidden="true" />
                <p className="mt-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
                  {query || statusFilter !== "all" ? "No orders match that" : orders.length === 0 ? "You haven't placed an order yet" : tab === "active" ? "No active orders" : "No past orders yet"}
                </p>
                <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
                  {query || statusFilter !== "all" ? "Try another order ID, catch name or status." : "Fresh catch is a few taps away."}
                </p>
                <Link to="/menu" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">Browse the menu</Link>
              </div>
            )}

            {shown.map((order) => {
              const packs = packCount(order);
              const step = STEPS.find((s) => s.status === order.status);
              return (
                <article key={order.ref} aria-label={`Order ${order.ref}`} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="font-mono text-sm font-bold text-ocean-950 dark:text-foam-50">{order.ref}</h2>
                      <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{headline(order)}</p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  {order.active && step && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl bg-ocean-700/5 p-3 text-xs dark:bg-white/5">
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                      <p className="text-ocean-800/90 dark:text-foam-100/85">
                        <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">{step.title}</span>
                        {step.detail} Placed {formatWhen(order.createdAt)}.
                      </p>
                    </div>
                  )}

                  <div className="mt-3 rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5"><OrderLines order={order} /></div>

                  <div className="mt-3 flex items-end justify-between gap-2 text-sm">
                    <div>
                      <p className="text-ocean-800/85 dark:text-foam-100/80">{packs} {packs === 1 ? "pack" : "packs"} · {order.items.length} {order.items.length === 1 ? "item" : "items"}</p>
                      <p className="flex items-center gap-1 text-xs font-semibold text-teal-700 dark:text-teal-300"><CircleCheck className="h-3.5 w-3.5" aria-hidden="true" /> Paid {paidVia(order)}</p>
                    </div>
                    <p className="text-ocean-800/85 dark:text-foam-100/80">{order.active ? "Total" : "Paid"}: <span className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">{formatInr(order.total)}</span></p>
                  </div>

                  {!order.active && <div className="mt-3"><RefundNote order={order} /></div>}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {order.active ? (
                      <CancelOrder order={order} onChanged={replace} />
                    ) : order.status === "delivered" ? (
                      <Link to={`/orders/${order.ref}/reorder`} className="inline-flex min-h-11 basis-full items-center justify-center gap-1.5 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
                        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Re-order catch<span className="sr-only"> from {order.ref}</span>
                      </Link>
                    ) : null}
                    <Link to={`/orders/${order.ref}/invoice`} className={secondary}>
                      <FileText className="h-4 w-4" aria-hidden="true" /> {order.active ? "Bill" : "Invoice"}<span className="sr-only"> for order {order.ref}</span>
                    </Link>
                    <Link to={`/orders/${order.ref}`} className={`${secondary} flex-1 ${order.active ? "bg-ocean-800 text-white hover:bg-ocean-700 dark:bg-ocean-800 dark:text-white" : ""}`}>
                      View details<span className="sr-only"> for order {order.ref}</span>
                    </Link>
                    {order.status === "delivered" && (
                      <Link to={`/orders/${order.ref}/review`} className={secondary}>
                        <Star className={`h-4 w-4 text-coral-500 ${order.review ? "fill-current" : ""}`} aria-hidden="true" />
                        {order.review ? `${order.review.overall}.0` : "Rate"}
                        <span className="sr-only"> {order.review ? "your review of" : "review"} order {order.ref}</span>
                      </Link>
                    )}
                    {order.status === "cancelled" && (
                      <Link to={`/orders/${order.ref}/reorder`} className={secondary}>
                        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Re-order<span className="sr-only"> {order.ref}</span>
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          <p className="text-center text-xs text-ocean-800/75 dark:text-foam-100/70">
            Need help with an order? <Link to="/contact" className="font-semibold underline">Contact us</Link> with the order ID.
          </p>
        </div>
      </div>
    </PageTransition>
  );
}
